import React from 'react';

function ResponsibleGambling() {
  return (
    <div className="page responsible-page">
      <section className="page-hero">
        <div className="container">
          <div className="page-hero-content">
            <span className="page-badge">Player Safety</span>
            <h1>Responsible Gambling</h1>
            <p>Keeping gambling fun, safe, and under control</p>
          </div>
        </div>
      </section>

      <section className="page-content">
        <div className="container">
          <div className="responsible-intro">
            <div className="age-warning-large">
              <span className="age-badge-xl">18+</span>
              <div>
                <h2>Gambling is for Adults Only</h2>
                <p>You must be 18 years or older to gamble in the United Kingdom.</p>
              </div>
            </div>
            <p className="lead">
              At SlotsHub UK, we believe gambling should be an enjoyable form of entertainment, 
              never a source of stress or financial difficulty. We're committed to promoting 
              responsible gambling and providing resources to help you stay in control.
            </p>
          </div>

          <div className="responsible-section">
            <h2>Safe Gambling Tips</h2>
            <div className="tips-grid">
              <div className="tip-card">
                <div className="tip-icon">💰</div>
                <h3>Set a Budget</h3>
                <p>
                  Before you start playing, decide how much you can afford to lose. Only gamble 
                  with money you don't need for essential expenses. When your budget is gone, stop.
                </p>
              </div>
              <div className="tip-card">
                <div className="tip-icon">⏱️</div>
                <h3>Set Time Limits</h3>
                <p>
                  Decide in advance how long you'll play. Use casino time limit tools or set alarms 
                  on your phone. Take regular breaks and don't let gambling take over other activities.
                </p>
              </div>
              <div className="tip-card">
                <div className="tip-icon">🚫</div>
                <h3>Never Chase Losses</h3>
                <p>
                  If you're losing, don't try to win it back by betting more. Chasing losses almost 
                  always leads to bigger losses. Accept that losing is part of gambling.
                </p>
              </div>
              <div className="tip-card">
                <div className="tip-icon">🎯</div>
                <h3>Understand the Odds</h3>
                <p>
                  The house always has an edge. Gambling is not a way to make money. Treat any 
                  winnings as a bonus, not an expectation.
                </p>
              </div>
              <div className="tip-card">
                <div className="tip-icon">😊</div>
                <h3>Gamble for Fun</h3>
                <p>
                  Gambling should be entertaining. If it stops being fun, or if you're gambling 
                  to escape problems or feelings, it's time to take a break.
                </p>
              </div>
              <div className="tip-card">
                <div className="tip-icon">🍺</div>
                <h3>Stay Sober</h3>
                <p>
                  Never gamble when under the influence of alcohol or drugs. They impair judgement 
                  and can lead to poor decisions and spending more than intended.
                </p>
              </div>
            </div>
          </div>

          <div className="responsible-section">
            <h2>Warning Signs of Problem Gambling</h2>
            <p>
              Gambling can become a problem when it starts to negatively affect your life. 
              Be honest with yourself – if any of these apply, please seek help:
            </p>
            <ul className="warning-signs">
              <li>Spending more money or time gambling than you intended</li>
              <li>Having difficulty stopping or cutting back</li>
              <li>Feeling restless or irritable when trying to stop gambling</li>
              <li>Gambling to escape problems or relieve anxiety, depression, or guilt</li>
              <li>Chasing losses by returning to gamble more</li>
              <li>Lying to family or friends about how much you gamble</li>
              <li>Borrowing money or selling possessions to fund gambling</li>
              <li>Jeopardising relationships, jobs, or opportunities because of gambling</li>
              <li>Relying on others to bail you out of financial troubles caused by gambling</li>
              <li>Continuing to gamble despite knowing it's causing problems</li>
            </ul>
          </div>

          <div className="responsible-section tools-section">
            <h2>Responsible Gambling Tools</h2>
            <p>
              All UK-licensed casinos are required to offer tools to help you stay in control. 
              Use these features – they're there for your protection.
            </p>
            <div className="tools-grid">
              <div className="tool-card">
                <h3>Deposit Limits</h3>
                <p>Set daily, weekly, or monthly limits on how much you can deposit.</p>
              </div>
              <div className="tool-card">
                <h3>Loss Limits</h3>
                <p>Limit how much you can lose over a set period.</p>
              </div>
              <div className="tool-card">
                <h3>Session Limits</h3>
                <p>Set how long you can play in a single session.</p>
              </div>
              <div className="tool-card">
                <h3>Reality Checks</h3>
                <p>Get regular reminders of how long you've been playing.</p>
              </div>
              <div className="tool-card">
                <h3>Cooling-Off Periods</h3>
                <p>Take a short break from gambling (24 hours to 6 weeks).</p>
              </div>
              <div className="tool-card">
                <h3>Self-Exclusion</h3>
                <p>Block yourself from gambling for longer periods.</p>
              </div>
            </div>
          </div>

          <div className="responsible-section gamstop-section">
            <h2>GAMSTOP: National Self-Exclusion</h2>
            <div className="gamstop-content">
              <img src="/logos/gamstop.jpg" alt="GAMSTOP" className="gamstop-logo" />
              <div>
                <p>
                  GAMSTOP is the UK's free national self-exclusion scheme. When you register with 
                  GAMSTOP, you'll be blocked from all UK-licensed gambling websites and apps for 
                  your chosen period: 6 months, 1 year, or 5 years.
                </p>
                <p>
                  Registration is free, confidential, and takes about 10 minutes. Once registered, 
                  you cannot reverse your decision until the exclusion period ends.
                </p>
                <a href="https://www.gamstop.co.uk/" target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                  Register with GAMSTOP
                </a>
              </div>
            </div>
          </div>

          <div className="responsible-section help-section">
            <h2>Get Help & Support</h2>
            <p>
              If you're worried about your gambling, or someone else's, free and confidential 
              help is available 24 hours a day, 7 days a week.
            </p>
            <div className="help-grid">
              <div className="help-card">
                <img src="/logos/begambleaware.svg" alt="BeGambleAware" />
                <h3>BeGambleAware</h3>
                <p>Free, confidential advice and support for anyone affected by gambling.</p>
                <a href="https://www.gambleaware.org/" target="_blank" rel="noopener noreferrer">
                  www.gambleaware.org
                </a>
              </div>
              <div className="help-card">
                <img src="/logos/gamcare.svg" alt="GamCare" />
                <h3>GamCare</h3>
                <p>Information, advice, and counselling for problem gamblers and their families.</p>
                <a href="https://www.gamcare.org.uk/" target="_blank" rel="noopener noreferrer">
                  www.gamcare.org.uk
                </a>
                <p className="helpline"><strong>Helpline: 0808 8020 133</strong></p>
              </div>
              <div className="help-card">
                <h3>Gamblers Anonymous</h3>
                <p>A fellowship of people who share their experience to help each other recover.</p>
                <a href="https://www.gamblersanonymous.org.uk/" target="_blank" rel="noopener noreferrer">
                  www.gamblersanonymous.org.uk
                </a>
              </div>
              <div className="help-card">
                <h3>National Problem Gambling Clinic</h3>
                <p>NHS specialist treatment service for problem gamblers.</p>
                <a href="https://www.cnwl.nhs.uk/services/mental-health-services/addictions-and-substance-misuse/national-problem-gambling-clinic" target="_blank" rel="noopener noreferrer">
                  NHS Problem Gambling Clinic
                </a>
              </div>
            </div>
          </div>

          <div className="responsible-section">
            <h2>For Friends and Family</h2>
            <p>
              If someone you care about has a gambling problem, you may be affected too. Support 
              is available for friends and family members:
            </p>
            <ul>
              <li><strong>GamCare</strong> offers support for anyone affected by gambling, including family and friends</li>
              <li><strong>Gam-Anon</strong> provides support groups specifically for family members of problem gamblers</li>
              <li>Consider seeking advice even if the person gambling doesn't want help yet</li>
              <li>Don't take on their debt or cover up their gambling</li>
              <li>Look after your own wellbeing and finances</li>
            </ul>
          </div>

          <div className="commitment-box">
            <h2>Our Commitment</h2>
            <p>
              SlotsHub UK is committed to promoting responsible gambling. We only recommend 
              UK-licensed casinos that meet regulatory standards for player protection. We 
              encourage you to use responsible gambling tools and seek help if needed.
            </p>
            <p>
              Gambling should be fun. When it stops being fun, it's time to stop.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ResponsibleGambling;
